import React, { useContext, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Checkbox,
  FormControlLabel,
  Button,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SuccessfulPage from "./SuccessfulPage";
import { advisoryLayerExecutable } from "../../../../services/webServices";
import LinearProgressBar from "../../../common-components/QMentisLinearProgressBar";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  WidthType,
  TableRow,
  TableCell,
  Table,
  TableOfContents,
} from "docx";
import { saveAs } from "file-saver";
import { MessagePopupContext } from "../../../main-pages/ServicePage";
import SessionExpired from "../../../popup-components/SessionExpiredPopup";

// Define the testing types for each QE Pillar
const testingTypes = {
  "Observability and Monitoring": [
    "Log-based Monitoring",
    "Event Simulation Testing",
    "Monitoring Tool Integration Testing",
    "Telemetry Testing",
    "Alerting and Notification Testing",
    "Performance Monitoring",
    "Application Health Monitoring",
  ],
  "Trust Zone Risk-Based QE": [
    "Risk-Based Test Prioritization",
    "Risk Analysis and Impact Assessment",
    "Threat Modeling Testing",
    "Security Risk Assessment",
    "Compliance Risk Testing",
    "Security Audits",
    "Penetration Testing",
  ],
  "Security Compliance Validation": [
    "Security Testing",
    "Vulnerability Scanning",
    "Compliance Testing (GDPR, HIPAA, etc.)",
    "Penetration Testing",
    "Privacy Testing",
    "Access Control Testing",
    "Encryption and Decryption Testing",
    "Legal Compliance Testing",
  ],
  "Strategic Outcome Metrics": [
    "KPI Tracking and Validation",
    "Business Scenario Testing",
    "Business Capability Mapping Testing",
    "Strategic Business Alignment Testing",
    "Outcome-Based Testing",
    "ROI and Performance Measurement Testing",
  ],
  "Performance & Scalability Testing": [
    "Load Testing",
    "Stress Testing",
    "Scalability Testing",
    "Performance Benchmarking",
    "Endurance Testing",
    "Throughput Testing",
    "Latency Testing",
    "Resource Utilization Testing",
  ],
  "Shift-Left Testing": [
    "Unit Testing",
    "Integration Testing",
    "Static Code Analysis",
    "Early Validation Testing",
    "Continuous Testing",
    "Test-Driven Development (TDD)",
    "Behavior-Driven Development (BDD)",
    "Continuous Integration Testing",
  ],
  "Test Automation and CI/CD": [
    "Automated Test Execution",
    "Continuous Integration Testing",
    "Regression Test Automation",
    "Test Automation Framework Development",
    "API Automation Testing",
    "UI Automation Testing",
    "End-to-End Automation",
  ],
  "Resilience & Scalability Testing": [
    "Load Testing",
    "Stress Testing",
    "Chaos Engineering",
    "Fault Injection Testing",
    "Resilience Testing",
    "High Availability Testing",
    "Recovery Testing",
    "Auto-Scaling Validation",
  ],
  "Business Capability Mapping": [
    "End-to-End Business Process Testing",
    "Business Scenario Validation",
    "Domain-Specific Testing",
    "Requirement Traceability Testing",
    "Capability and Feature Testing",
    "Business Impact Analysis Testing",
  ],
  "Test Environment Testing": [
    "Environment Configuration Validation",
    "Test Data Integrity Testing",
    "Test Setup Automation",
    "Test Environment Stability Testing",
    "Multi-Environment Consistency Testing",
    "Load Testing on Test Environments",
    "Test Environment Security Assessment",
  ],
  "CX Usability Testing": [
    "User Interface Testing",
    "User Experience (UX) Testing",
    "A/B Testing",
    "Usability Testing",
    "Accessibility Testing",
    "Cross-Browser and Cross-Device Testing",
    "Persona-Based Testing",
    "Cognitive Load Testing",
  ],
  "Data-Driven Testing": [
    "Data Validation Testing",
    "Data Consistency Testing",
    "Data Migration Testing",
    "Data Synchronization Testing",
    "Data Integrity Testing",
    "Data Conversion Testing",
    "Data Profiling",
    "Data Quality Monitoring",
  ],
  "AI/ML Model Testing": [
    "Model Accuracy and Performance Testing",
    "Fairness and Bias Testing",
    "Model Explainability Validation",
    "AI Model Deployment Testing",
    "Ethical Compliance Testing for AI",
    "Data Privacy in AI Testing",
    "Model Drift Monitoring",
    "Model Interpretability Testing",
  ],
};

const pillarToStrategyMap = {
  "AI/ML Model Testing": "AI ML LLM Governance",
  "Test Automation and CI/CD": "Automation All Levels",
  "Shift-Left Testing": "Shift Left Continuous Testing",
  "Data-Driven Testing": "Data Quality Integrity",
  "Observability and Monitoring": "Observability Monitoring",
  "Performance & Scalability Testing": "Performance Engineering",
  "Test Environment Testing": "Test Environment Test Data Management",
  "CX Usability Testing": "CX Usability",
  "Business Capability Mapping": "Business Capability Mapping",
  "Security Compliance Validation": "Security Compliance Validation",
  "Strategic Outcome Metrics": "Strategic Outcome Metrics",
  "Resilience & Scalability Testing": "Resilience Scalability Testing",
};

export const SelectedQEPillars = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selected, updatedCSV, updatedConfidences } = location.state || {};

  // Drawer state
  const [openDrawer, setOpenDrawer] = useState(false);

  // State to track selected test types for each pillar
  const [selectedTestTypes, setSelectedTestTypes] = useState({});

  const [loader, setLoader] = useState(false);

  const [documentBlob, setDocumentBlob] = useState(null);

  const [sessionExpiredValue, setSessionExpiredValue] = useState(false);

  const { setMessagePopup, setMessage } = useContext(MessagePopupContext);

  // Helper function to get selected test count for a pillar
  const getSelectedTestCount = (pillar) => {
    return selectedTestTypes[pillar]?.length || 0;
  };

  // Build sections from advisoryLayerExecutable JSON
  const buildSections = (content) => {
    if (!content?.sections) return [];
    const children = [];

    let currentSectionNum = null;
    let hasAddedSectionHeader = false;

    // Sort section keys to ensure proper order (0.1, 0.2, 1.1, 1.2, etc.)
    const sortedKeys = Object.keys(content.sections).sort((a, b) => {
      const [aMajor, aMinor] = a.split(".").map(Number);
      const [bMajor, bMinor] = b.split(".").map(Number);
      if (aMajor !== bMajor) return aMajor - bMajor;
      return aMinor - bMinor;
    });

    const sectionTitles = {
      0: "Executive CXO Summary",
      1: "Enterprise Archetype & Strategic Context",
      2: "Requirements--Architecture--Quality Alignment",
      3: "Architecture-Aware Quality Mapping",
      4: "Business Value Objectives & QE Value Flow",
      5: "Confidence Engineering Framework",
      6: "Non-Functional Quality & Runtime Resilience",
      7: "Quality Risk Intelligence & Resilience Strategy",
      8: "QE Operating Model & Roles",
      9: "QE Technology Stack & Platform Integration",
      10: "Metrics, Dashboards & Executive Reporting",
      11: "Roadmap, Rollout & Strategic Outcomes",
      12: "Appendix, Tools, Glossary & Credits",
    };

    sortedKeys.forEach((key) => {
      const section = content.sections[key];
      const sectionMatch = key.match(/^(\d+)\./);
      const sectionNum = sectionMatch ? sectionMatch[1] : null;

      // Add section header when entering a new major section
      if (sectionNum !== currentSectionNum) {
        currentSectionNum = sectionNum;
        hasAddedSectionHeader = false;
      }

      if (!hasAddedSectionHeader && sectionNum !== null) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `Section ${sectionNum}: ${
                  sectionTitles[sectionNum] || "Unknown Section"
                }`,
                bold: true,
                size: 32,
                font: "Calibri",
                color: "000000",
              }),
            ],
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 300 },
          })
        );
        hasAddedSectionHeader = true;
      }

      const isExecutiveSection = key.startsWith("0.");

      // Add subsection title
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `${key} ${section.title}`,
              bold: true,
              size: isExecutiveSection ? 28 : 26,
              font: "Calibri",
              color: isExecutiveSection ? "0066CC" : "000000",
            }),
          ],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 300, after: 200 },
        })
      );

      // Add summary paragraphs
      if (section.summary) {
        const summaryParagraphs = section.summary
          .split(/\n\s*\n/)
          .filter((para) => para.trim().length > 0);

        summaryParagraphs.forEach((para) => {
          const trimmedPara = para.trim();

          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: trimmedPara,
                  size: 22,
                  font: "Calibri",
                }),
              ],
              spacing: { before: 100, after: 200 },
              alignment: AlignmentType.JUSTIFIED,
            })
          );
        });
      }

      // Add bullets if present
      if (
        section.bullets &&
        Array.isArray(section.bullets) &&
        section.bullets.length > 0
      ) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Key Initiatives:",
                bold: true,
                size: 24,
                font: "Calibri",
              }),
            ],
            spacing: { before: 200, after: 100 },
          })
        );

        section.bullets.forEach((bullet) => {
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: bullet,
                  size: 22,
                  font: "Calibri",
                }),
              ],
              bullet: {
                level: 0,
              },
              spacing: { before: 100, after: 100 },
            })
          );
        });
      }

      // Add tables if present
      if (
        section.tables &&
        Array.isArray(section.tables) &&
        section.tables.length > 0
      ) {
        section.tables.forEach((tableData) => {
          if (tableData.title) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: tableData.title,
                    bold: true,
                    size: 24,
                    font: "Calibri",
                  }),
                ],
                spacing: { before: 300, after: 150 },
              })
            );
          }

          const tableRows = [];

          // Header row
          if (tableData.columns && tableData.columns.length > 0) {
            tableRows.push(
              new TableRow({
                children: tableData.columns.map(
                  (col) =>
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: col,
                              bold: true,
                              size: 22,
                              font: "Calibri",
                            }),
                          ],
                        }),
                      ],
                      shading: {
                        fill: "D9E2F3",
                      },
                    })
                ),
              })
            );
          }

          // Data rows
          if (tableData.rows && Array.isArray(tableData.rows)) {
            tableData.rows.forEach((row) => {
              tableRows.push(
                new TableRow({
                  children: row.map(
                    (cell) =>
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: String(cell || ""),
                                size: 22,
                                font: "Calibri",
                              }),
                            ],
                          }),
                        ],
                      })
                  ),
                })
              );
            });
          }

          // Add table to document
          children.push(
            new Table({
              rows: tableRows,
              width: {
                size: 100,
                type: WidthType.PERCENTAGE,
              },
            })
          );

          // Add spacing after table
          children.push(
            new Paragraph({
              children: [new TextRun({ text: "" })],
              spacing: { before: 200, after: 200 },
            })
          );
        });
      }
    });

    return children;
  };

  const handleCheckboxChange = (pillar, testType) => {
    setSelectedTestTypes((prev) => {
      const pillarTests = prev[pillar] || [];
      const isSelected = pillarTests.includes(testType);

      return {
        ...prev,
        [pillar]: isSelected
          ? pillarTests.filter((t) => t !== testType)
          : [...pillarTests, testType],
      };
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleProceed = async () => {
    setLoader(true);

    try {
      // Improved CSV parser that handles quotes correctly
      const parseCSVLine = (line) => {
        const result = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          const nextChar = line[i + 1];

          if (char === '"') {
            if (inQuotes && nextChar === '"') {
              current += '"';
              i++;
            } else {
              inQuotes = !inQuotes;
            }
          } else if (char === "," && !inQuotes) {
            result.push(current);
            current = "";
          } else {
            current += char;
          }
        }
        result.push(current);
        return result;
      };

      const rows = updatedCSV.split("\n").filter((row) => row.trim());

      if (rows.length < 2) {
        throw new Error("CSV must have at least header and data rows");
      }

      const headers = parseCSVLine(rows[0]);
      const dataRow = parseCSVLine(rows[1]);

      // Ensure both arrays have the same length
      const maxLength = Math.max(headers.length, dataRow.length);
      while (headers.length < maxLength) headers.push("");
      while (dataRow.length < maxLength) dataRow.push("");

      // Create a clean mapping of headers for better matching
      const headerMap = new Map();
      headers.forEach((header, index) => {
        const cleanHeader = header.trim().replace(/^"|"$/g, "").trim();
        headerMap.set(cleanHeader, index);
      });

      // Create copies for updating
      const newDataRow = [...dataRow];

      // For each selected pillar, find and update the column
      selected?.forEach((pillar) => {
        const cleanPillar = pillar.trim();

        // Try exact match first
        let pillarIndex = headerMap.get(cleanPillar);

        // If no exact match, try case-insensitive match
        if (pillarIndex === undefined) {
          for (const [header, index] of headerMap.entries()) {
            if (header.toLowerCase() === cleanPillar.toLowerCase()) {
              pillarIndex = index;
              break;
            }
          }
        }

        if (pillarIndex !== undefined && pillarIndex < newDataRow.length) {
          const selectedCount = getSelectedTestCount(pillar);

          // If no tests selected, use all original tests; otherwise use selected tests
          const tests =
            selectedCount === 0
              ? testingTypes[pillar] || []
              : selectedTestTypes[pillar] || [];

          newDataRow[pillarIndex] = tests.length > 0 ? tests.join("; ") : "";
        } else {
          console.warn(
            `Pillar "${cleanPillar}" not found in headers or index out of bounds`
          );
        }
      });

      // IMPORTANT: Clear any pillars that were NOT selected
      // This prevents leftover test types from appearing
      headers.forEach((header, index) => {
        const cleanHeader = header.trim().replace(/^"|"$/g, "").trim();
        // If this header is a QE pillar but NOT in the selected list
        if (testingTypes[cleanHeader] && !selected?.includes(cleanHeader)) {
          newDataRow[index] = "";
        }
      });

      // Reconstruct CSV with proper quoting
      const escapeCSVField = (field) => {
        const fieldStr = String(field || "");
        // Always quote fields that contain special characters
        if (
          fieldStr.includes(",") ||
          fieldStr.includes('"') ||
          fieldStr.includes("\n") ||
          fieldStr.includes("\r") ||
          fieldStr.includes(";")
        ) {
          return `"${fieldStr.replace(/"/g, '""')}"`;
        }
        return fieldStr;
      };

      const updatedCSVContent = [
        headers
          .map((h) => escapeCSVField(h.replace(/^"|"$/g, "").trim()))
          .join(","),
        newDataRow.map(escapeCSVField).join(","),
      ].join("\n");

      // Create a Blob for the updated CSV
      const csvBlob = new Blob(["\ufeff", updatedCSVContent], {
        type: "text/csv;charset=utf-8;",
      });

      // Prepare FormData for API call
      const formData = new FormData();
      formData.append("file", csvBlob, "qe_strategy_report_final.csv");

      // Download CSV
      const csvLink = document.createElement("a");
      csvLink.href = URL.createObjectURL(csvBlob);
      csvLink.download = "qe_strategy_report_final.csv";
      csvLink.style.visibility = "hidden";
      document.body.appendChild(csvLink);
      csvLink.click();
      document.body.removeChild(csvLink);
      URL.revokeObjectURL(csvLink.href);

      try {
        const response = await advisoryLayerExecutable(formData);

        const content = response.data;
        const sections = buildSections(content);

        // Create Cover Page
        const coverPage = [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 600 },
            children: [
              new TextRun({
                text: "QE Strategy — Enterprise Report",
                bold: true,
                size: 48,
                font: "Calibri",
                color: "0A1F44",
              }),
            ],
          }),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { before: 100, after: 100 },
            children: [
              new TextRun({
                text: `Generated: ${new Date()
                  .toISOString()
                  .slice(0, 16)
                  .replace("T", " ")} UTC`,
                size: 24,
                font: "Calibri",
              }),
            ],
          }),
        ];

        const tocPage = [
          new Paragraph({
            text: "Table of Contents",
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
          }),

          new TableOfContents(" ", {
            hyperlink: true,
            headingStyleRange: "1-5",
          }),
        ];

        const doc = new Document({
          styles: {
            default: {
              document: {
                run: { font: "Calibri", size: 22 },
              },
            },
          },

          // ⭐⭐ THIS enables automatic TOC update (real page numbers)
          features: {
            updateFields: true,
          },

          sections: [
            {
              properties: { titlePage: true },
              children: coverPage,
            },
            {
              children: tocPage,
            },
            {
              properties: {
                page: {
                  margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
                },
              },
              children: sections,
            },
          ],
        });

        const blob = await Packer.toBlob(doc);
        setDocumentBlob(blob);

        setLoader(false);
        setOpenDrawer(true);
      } catch (error) {
        console.error("Full error:", error);
        console.error("Error response:", error.response);
        setLoader(false);

        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Unknown error occurred";

        setMessage(errorMessage);
        setMessagePopup(true);

        if (errorMessage === "Your session has expired.") {
          setSessionExpiredValue(true);
        }
      }
    } catch (error) {
      console.error("Processing Error:", error);
      alert(
        "An error occurred while processing the CSV. Please check the format and try again."
      );
      setLoader(false);
    }
  };

  return (
    <Box>
      <Box p={3} maxWidth="100%" mx="auto">
        <Typography
          variant="h6"
          mb={2}
          sx={{ fontWeight: 600, fontSize: "20px", fontStyle: "SemiBold" }}
        >
          Human in the loop Suggested QE Pillars
        </Typography>

        <Grid container spacing={3}>
          {selected?.map((pillar, index) => {
            // Find the matching confidence
            const strategyName = pillarToStrategyMap[pillar] || pillar;

            let confidenceObj = updatedConfidences?.find(
              (c) =>
                c.strategy.toLowerCase().trim() ===
                strategyName.toLowerCase().trim()
            );

            if (!confidenceObj) {
              confidenceObj = updatedConfidences?.find((c) => {
                const pillarWords = pillar.toLowerCase().split(/\s+/);
                const strategyWords = c.strategy.toLowerCase().split(/\s+/);
                const matchCount = strategyWords.filter((w) =>
                  pillarWords.includes(w)
                ).length;
                return matchCount >= Math.ceil(strategyWords.length / 2);
              });
            }

            return (
              <Grid
                item
                xs={12}
                sm={6}
                key={index}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <Paper
                  sx={{
                    padding: 3,
                    borderRadius: 2,
                    boxShadow: 2,
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      marginBottom: 1,
                      fontSize: "20px",
                      fontStyle: "SemiBold",
                    }}
                  >
                    {pillar}
                  </Typography>

                  <Grid container sx={{ color: "#3f3939de" }}>
                    {testingTypes[pillar]?.map((testType, idx) => (
                      <Grid item xs={12} key={idx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                selectedTestTypes[pillar]?.includes(testType) ||
                                false
                              }
                              onChange={() =>
                                handleCheckboxChange(pillar, testType)
                              }
                            />
                          }
                          label={testType}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.9rem",
                            },
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* <Box
        mt={4}
        display="flex"
        justifyContent="space-between"
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "#f6f3f2",
          padding: 1.2,
          paddingLeft: 3,
          paddingRight: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Button
          variant="outlined"
          sx={{
            minWidth: 110,
            fontSize: "16px",
            border: "2px solid #0094FB",
            borderRadius: "8px",
            gap: "8px",
            textTransform: "none",
          }}
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          variant="contained"
          sx={{
            minWidth: 110,
            fontSize: "16px",
            backgroundColor: "#0094FB",
            border: "2px solid #0094FB",
            borderRadius: "8px",
            gap: "8px",
            textTransform: "none",
          }}
          endIcon={<ArrowForwardIcon />}
          onClick={handleProceed}
        >
          Proceed
        </Button>

      </Box> */}

      <Box
        mt={4}
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "#f6f3f2",
          padding: 1.2,
          paddingLeft: 3,
          paddingRight: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Left: Back button */}
        <Button
          variant="outlined"
          sx={{
            minWidth: 110,
            textTransform: "none",
            fontSize: "16px",
            border: "2px solid #0094FB",
            borderRadius: "8px",
            gap: "8px",
          }}
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back
        </Button>

        {/* Right: Proceed button + disclaimer vertically stacked */}
        <Box display="flex" flexDirection="column" alignItems="center">
          <Button
            variant="contained"
            sx={{
              minWidth: 110,
              fontSize: "16px",
              backgroundColor: "#0094FB",
              textTransform: "none",
              border: "2px solid #0094FB",
              borderRadius: "8px",
              gap: "8px",
              marginLeft: "150px", // 👈 shift the whole Proceed+Text group right
            }}
            endIcon={<ArrowForwardIcon />}
            onClick={handleProceed}
          >
            Proceed
          </Button>
        </Box>
      </Box>
      {loader && <LinearProgressBar label={"Analyzing..."} />}

      {sessionExpiredValue && <SessionExpired />}

      {/* Drawer for SuccessfulPage */}
      <SuccessfulPage
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        documentBlob={documentBlob}
      />
    </Box>
  );
};
